import { addYears, formatISO } from 'date-fns';
import i18n from '@fintronners/react-language/src/Lang';
import {
  OrderDirection,
  SecurityAssetOrderField,
  SecurityAssetEdgeColumn,
  SecurityFixedIncomeDetailsFixedIncomeType,
  SecurityFixedIncomeDetailsFixedIncomeSubtype,
  SecurityAsset,
  UserAccountPortfolioEdge,
  SecurityAssetSecurityType,
} from '@fintronners/react-api/src';
import { isBradesco } from '@fintronners/react-utils/src/tenantUtils';

import {
  ETF_SECURITY_TYPES,
  MUTUAL_FUND_SECURITY_TYPES,
  SECURITY_CATEGORY,
  STOCK_SECURITY_TYPES,
  FIXED_INCOME_SECURITY_TYPES,
  BOND_TYPE_TREASURY_OPTION_KEY,
  BOND_TYPE_TREASURY_RELATED_OPTIONS_KEYS,
  BOND_TYPE_OPTIONS,
  MATURITY_OPTIONS_KEYS,
  LOAD_TYPE_OPTIONS,
} from './constants';

import type {
  SecurityCategory,
  SortSelectOptionValue,
  BondTypeCheckState,
  BondTypeOptionKey,
  MaturityOptionKey,
  MaturityFilterVariables,
  LoadTypeOptionKey,
} from './types';
import { getBondAssetPrice } from '../account/portfolio/utils';
import { convertISODateStringToDisplayString } from '@fintronners/react-utils/src/dateUtils';
import { NumberNoNaN } from '@fintronners/react-utils/src/numberUtilsTSX';

import type { BondTypeDropdownCheckboxOption } from './hooks/useBondTypeOptions';

export const getSecurityTypesByCategory = (securityCategory: SecurityCategory) => {
  if (securityCategory === SECURITY_CATEGORY.STOCK) {
    return STOCK_SECURITY_TYPES;
  }

  if (securityCategory === SECURITY_CATEGORY.ETF) {
    return ETF_SECURITY_TYPES;
  }

  if (securityCategory === SECURITY_CATEGORY.MUTUAL_FUND) {
    return MUTUAL_FUND_SECURITY_TYPES;
  }

  if (securityCategory === SECURITY_CATEGORY.FIXED_INCOME) {
    return FIXED_INCOME_SECURITY_TYPES;
  }

  return undefined;
};

export const parseSortOption = (sortOption: SortSelectOptionValue | undefined) => {
  return (sortOption?.split('-') ?? []) as [SecurityAssetOrderField, OrderDirection];
};

export const getSecurityCategoryTitle = (category: SecurityCategory): string => {
  if (category === SECURITY_CATEGORY.STOCK) return i18n.t('common.securityCategory.stocks');
  if (category === SECURITY_CATEGORY.ETF) return i18n.t('common.securityCategory.etfs');

  if (category === SECURITY_CATEGORY.MUTUAL_FUND)
    return i18n.t('common.securityCategory.mutualFunds');

  if (category === SECURITY_CATEGORY.FIXED_INCOME)
    return i18n.t('common.securityCategory.fixedIncome');

  return '';
};

export const getYesNoDisplayValue = (value: boolean | undefined): string => {
  if (typeof value === 'boolean') {
    return value ? i18n.t('common.yes') : i18n.t('common.no');
  }

  return i18n.t('common.callable');
};

export const getBondTypeCheckState = (
  currentState: BondTypeCheckState,
  key: BondTypeOptionKey,
  isChecked: boolean,
): Record<BondTypeOptionKey, boolean> => {
  const isTreasuryOption = key === BOND_TYPE_TREASURY_OPTION_KEY;
  const isTreasuryRelatedOption = BOND_TYPE_TREASURY_RELATED_OPTIONS_KEYS.includes(
    key as (typeof BOND_TYPE_TREASURY_RELATED_OPTIONS_KEYS)[number],
  );

  const newState = { ...currentState };

  if (isTreasuryOption) {
    for (const k in newState) {
      newState[k as keyof typeof newState] = k === key ? isChecked : false;
    }

    BOND_TYPE_TREASURY_RELATED_OPTIONS_KEYS.forEach((o) => {
      newState[o] = isChecked;
    });

    return newState;
  }

  if (isTreasuryRelatedOption) {
    newState[key] = isChecked;
    return newState;
  }

  for (const k in newState) {
    newState[k as keyof typeof newState] = k === key ? isChecked : false;
  }

  return newState;
};

export const excludeBondTypeOptions = (
  initialOptions: BondTypeDropdownCheckboxOption[],
  excludeList: BondTypeOptionKey[],
) => {
  return initialOptions.filter((o) => !excludeList.includes(o.key));
};

export const getBondTypeInitialState = () => {
  return Object.keys(BOND_TYPE_OPTIONS).reduce((acc, key) => {
    acc[key as keyof typeof BOND_TYPE_OPTIONS] = false;
    return acc;
  }, {} as BondTypeCheckState);
};

export const getIsValidSortingValue = (sortingValue: string) => {
  const sortingValues = [
    ...Object.values(SecurityAssetOrderField),
    ...Object.values(SecurityAssetEdgeColumn),
  ];

  return sortingValues.includes(sortingValue as (typeof sortingValues)[number]);
};

export const getIsMobileSortOptionAvailable = (sortingValue: string) => {
  if (sortingValue === SecurityAssetOrderField.Symbol) return false;
  return true;
};

export const getMaturityFilterVariables = (
  option: MaturityOptionKey | null | undefined,
): MaturityFilterVariables | null => {
  const today = new Date();

  const dateInYears = (years: number) =>
    formatISO(addYears(today, years), { representation: 'date' });

  if (option === MATURITY_OPTIONS_KEYS.LESS_THAN_ONE_YEAR) {
    return {
      maturityDateGTE: undefined,
      maturityDateLTE: dateInYears(1),
    };
  }

  if (option === MATURITY_OPTIONS_KEYS.ONE_TO_THREE_YEARS) {
    return {
      maturityDateGTE: dateInYears(1),
      maturityDateLTE: dateInYears(3),
    };
  }

  if (option === MATURITY_OPTIONS_KEYS.THREE_TO_FIVE_YEARS) {
    return {
      maturityDateGTE: dateInYears(3),
      maturityDateLTE: dateInYears(5),
    };
  }

  if (option === MATURITY_OPTIONS_KEYS.FIVE_TO_TEN_YEARS) {
    return {
      maturityDateGTE: dateInYears(5),
      maturityDateLTE: dateInYears(10),
    };
  }

  if (option === MATURITY_OPTIONS_KEYS.TEN_TO_TWENTY_YEARS) {
    return {
      maturityDateGTE: dateInYears(10),
      maturityDateLTE: dateInYears(20),
    };
  }

  if (option === MATURITY_OPTIONS_KEYS.TWENTY_PLUS_YEARS) {
    return {
      maturityDateGTE: dateInYears(20),
      maturityDateLTE: undefined,
    };
  }

  return null;
};

export const getBondTypeSubtypeVariables = (
  bondTypeCheckState: BondTypeCheckState,
): [
  SecurityFixedIncomeDetailsFixedIncomeType[],
  SecurityFixedIncomeDetailsFixedIncomeSubtype[],
] => {
  const checked = Object.keys(bondTypeCheckState).filter(
    (k) => bondTypeCheckState[k as keyof typeof bondTypeCheckState],
  );

  const checkedTypes = checked.filter((k): k is SecurityFixedIncomeDetailsFixedIncomeType =>
    Object.values(SecurityFixedIncomeDetailsFixedIncomeType).includes(
      k as SecurityFixedIncomeDetailsFixedIncomeType,
    ),
  );

  const checkedSubTypes = checked.filter((k): k is SecurityFixedIncomeDetailsFixedIncomeSubtype =>
    Object.values(SecurityFixedIncomeDetailsFixedIncomeSubtype).includes(
      k as SecurityFixedIncomeDetailsFixedIncomeSubtype,
    ),
  );

  return [checkedTypes, checkedSubTypes];
};

export const getBondSymbol = (symbol: string | undefined) => {
  if (!symbol) return '';
  return symbol.split(' ').at(0) ?? '';
};

export const getBondShortName = (symbol: string | undefined, maturityDate: string | undefined) => {
  if (!symbol || !maturityDate) return '';

  const formattedSymbol = getBondSymbol(symbol);
  const formattedMaturityDate = convertISODateStringToDisplayString(maturityDate, '/', '2-digit');

  return `${formattedSymbol} ${formattedMaturityDate}`;
};

export const getBondHoldings = (
  securityNode: SecurityAsset | undefined,
  accountPortfolioEdges: UserAccountPortfolioEdge[] | undefined,
) => {
  let totalValue = 0;
  let totalShares = 0;
  let totalCost = 0;

  accountPortfolioEdges?.forEach((accountPortfolioEdge) => {
    totalValue += Number(accountPortfolioEdge?.node?.totalValue ?? 0);

    accountPortfolioEdge?.node?.assetBalances?.edges?.forEach((assetBalancesEdge) => {
      const shares = NumberNoNaN(assetBalancesEdge?.node?.balances?.activeBalance);
      const costBasis = NumberNoNaN(assetBalancesEdge?.node?.costBasis);
      const cost = costBasis * shares;

      totalShares += shares;
      totalCost += cost;
    });
  });

  const activeBalance =
    accountPortfolioEdges?.[0]?.node?.assetBalances?.edges?.[0]?.node?.balances?.activeBalance;

  const quantity = NumberNoNaN(activeBalance);
  const price = securityNode ? getBondAssetPrice(securityNode as SecurityAsset) : 0;
  const holdings = quantity * price;

  const finalCostBasis = totalCost / totalShares;
  const acquisitionCost = totalShares * finalCostBasis;
  const allTimeGainLoss = holdings - acquisitionCost;
  const allTimeGainLossPercent = (allTimeGainLoss / acquisitionCost) * 100;

  return {
    price,
    quantity,
    holdings,
    percentHoldings: holdings / totalValue,
    costBasis: finalCostBasis,
    allTimeGainLoss,
    allTimeGainLossPercent,
  };
};

const isLoadTypeOptionKey = (value: string): value is LoadTypeOptionKey => {
  return value in LOAD_TYPE_OPTIONS;
};

export const getLoadTypeDisplayValue = (
  loadType: LoadTypeOptionKey | string | undefined,
): string => {
  if (!loadType || !isLoadTypeOptionKey(loadType)) {
    return '';
  }

  const loadTypeOption = LOAD_TYPE_OPTIONS[loadType];
  return i18n.t(`miscScreens.securities.filterOptions.loadType.${loadTypeOption}`, loadTypeOption);
};

/**
 * Gets the display symbol for a security.
 * For L3 mutual funds, returns ISIN instead of symbol.
 * Currently only Bradesco has L3 funds, which is why we check for the Bradesco tenant.
 *
 * @param security The security asset
 * @returns ISIN for L3 mutual funds (Bradesco), otherwise symbol
 */
export const getSecurityDisplaySymbol = (
  security: Pick<SecurityAsset, 'symbol' | 'isin' | 'securityType'>,
): string => {
  const isMutualFund = security.securityType === SecurityAssetSecurityType.MutualFund;

  // ISIN is specifically for L3 mutual funds
  // Currently only Bradesco has L3 funds
  if (isBradesco && isMutualFund && security.isin) {
    return security.isin;
  }

  return security.symbol;
};
